import { type FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config';
import { AccountType } from './schemas/accountsBackendCodec';
import {
    type ExternalUser,
    type User,
    type UserGroup,
    type UserGroupsResponse,
    type AccountSetting,
    decodeAndConvertAdminUsersApiResponse,
    decodeUsersApiResponse,
    decodeUserGroupsResponse,
    decodeUserResponse,
    decodeAccountSettingResponse,
    mapExternalToInternalUser,
} from './schemas/userAdminBackendCodec';
import { RIO_AUDIT_LOG_HEADER, prepareHeaders, responseHandler } from './utils';

const getUserPutResource = (userId: string, user: UserMerged) => {
    // biome-ignore lint/suspicious/noExplicitAny: suppressed out of convenience for migration to biomejs
    const userRequestBody: { [key: string]: any } = {
        id: userId,
        account_id: user.accountId,
        first_name: user.firstName,
        last_name: user.lastName,
        group_ids: user.groupIds,
        preferred_language: user.preferredLanguage ? user.preferredLanguage : 'en-GB',
    };
    if (user.email === undefined) {
        // while it is generally possible to invite users with phone number as login, we currently only support inviting users via email
        throw new Error('Invalid user. Email has to be provided');
    }
    userRequestBody.preferred_login = 'email';
    userRequestBody.email = user.email;
    if (user.phoneNumber) {
        userRequestBody.phone_number = user.phoneNumber;
    }
    return userRequestBody;
};

const getAdminGroup = (accountType: string | undefined, userGroupsResponse: UserGroupsResponse) => {
    switch (accountType) {
        case AccountType.CARRIER:
            return userGroupsResponse.items.find(it => it.name === 'Fleet admin');
        case AccountType.SUPPLIER:
            return userGroupsResponse.items.find(it => it.name === 'Supplier admin');
        case AccountType.BUYER:
            return userGroupsResponse.items.find(it => it.name === 'Buyer admin');
        default:
            return userGroupsResponse.items.find(it => it.name === 'Fleet admin');
    }
};

export const usersApi = createApi({
    reducerPath: 'usersApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.USERADMIN_SERVICE, prepareHeaders }),
    tagTypes: ['User', 'AccountSetting'],
    endpoints: build => ({
        fetchUser: build.query<ExternalUser, { userId: string }>({
            query: ({ userId }) => ({
                url: `/users/${userId}`,
                responseHandler,
            }),
            transformResponse: (rawResult: unknown) => decodeUserResponse(rawResult),
        }),
        inviteUser: build.mutation<void, { user: UserMerged; reason: string }>({
            query: ({ user, reason }) => {
                const userId = crypto.randomUUID();
                // biome-ignore lint/suspicious/noExplicitAny: suppressed out of convenience for migration to biomejs
                const userRequestBody = getUserPutResource(userId, user as any);

                return {
                    url: `/users/${userId}`,
                    method: 'PUT',
                    body: userRequestBody,
                    headers: {
                        [RIO_AUDIT_LOG_HEADER]: reason,
                        'If-None-Match': '*', // if this header is not present, the request will fail with 404
                    },
                    responseHandler,
                };
            },
            invalidatesTags: ['User'],
        }),
        deleteUser: build.mutation<void, { userId: string; reason: string }>({
            query: ({ userId, reason }) => ({
                url: `/users/${userId}`,
                method: 'DELETE',
                headers: { [RIO_AUDIT_LOG_HEADER]: reason },
                responseHandler,
            }),
        }),
        fetchGroupsInAccount: build.query<UserGroup[], { accountId: string }>({
            queryFn: async ({ accountId }, _queryApi, _extraOptions, fetchBQ) => {
                let nextUrl: string | undefined = `/groups?account_id=${encodeURIComponent(accountId)}&limit=1000`;
                const groups: UserGroup[] = [];

                while (nextUrl) {
                    const result = await fetchBQ({ url: nextUrl, responseHandler });
                    if (result.error) {
                        return { error: result.error as FetchBaseQueryError };
                    }

                    const response = decodeUserGroupsResponse(result.data);
                    groups.push(...response.items);
                    nextUrl = response._links?.next?.href;
                }

                return { data: groups };
            },
        }),

        fetchAdminUsers: build.query<User[], { emailOrPhone: string }>({
            query: ({ emailOrPhone }) => ({
                url: `/admin/users/${encodeURIComponent(emailOrPhone)}`,
                responseHandler,
            }),
            transformResponse: decodeAndConvertAdminUsersApiResponse,
            providesTags: ['User'],
        }),
        fetchUsersByAccountId: build.query<User[], { accountId: string }>({
            queryFn: async ({ accountId }, _queryApi, _extraOptions, fetchBQ) => {
                let nextUrl: string | undefined = `/users?account_id=${encodeURIComponent(accountId)}&limit=1000`;
                const users: User[] = [];

                while (nextUrl) {
                    const result = await fetchBQ({ url: nextUrl, responseHandler });
                    if (result.error) {
                        return { error: result.error as FetchBaseQueryError };
                    }

                    const response = decodeUsersApiResponse(result.data);
                    users.push(...response.items.map(mapExternalToInternalUser));
                    nextUrl = response._links?.next?.href;
                }

                return { data: users };
            },
            providesTags: ['User'],
        }),
        fetchAccountSetting: build.query<AccountSetting, { accountId: string }>({
            query: ({ accountId }) => ({
                url: `/account-settings/${accountId}`,
                responseHandler: async (response: Response) => {
                    const data = await response.json();
                    return {
                        ...data,
                    };
                },
            }),
            transformResponse: (rawResult: unknown) => decodeAccountSettingResponse(rawResult),
            providesTags: ['AccountSetting'],
        }),
        inviteAdmin: build.mutation<null, { user: UserMerged; reason: string }>({
            queryFn: async (arg, _queryApi, _extraOptions, fetchBQ) => {
                const { user, reason } = arg;
                const groupsLimit = 1000;

                const userGroupsResult = await fetchBQ({
                    url: `/groups?account_id=${user.accountId}&limit=${groupsLimit}`,
                    responseHandler,
                });
                if (userGroupsResult.error) {
                    return { error: userGroupsResult.error as FetchBaseQueryError };
                }
                const userGroupsResponse = decodeUserGroupsResponse(userGroupsResult.data);
                if (userGroupsResponse.items.length === groupsLimit) {
                    return {
                        error: {
                            status: 'FETCH_ERROR',
                            data: undefined,
                            error: `Too many (=${groupsLimit}) groups found. Please contact Team Network.`,
                        },
                    };
                }

                const adminGroup = getAdminGroup(user.accountType, userGroupsResponse);
                if (!adminGroup) {
                    return {
                        error: {
                            status: 'FETCH_ERROR',
                            data: undefined,
                            error: 'Could not find fleet admin group',
                        },
                    };
                }

                const adminToInvite = {
                    ...user,
                    groupIds: [adminGroup.id],
                };

                const userId = crypto.randomUUID();
                const userPutResource = getUserPutResource(userId, adminToInvite);

                const result = await fetchBQ({
                    url: `/users/${userId}`,
                    method: 'PUT',
                    body: userPutResource,
                    headers: {
                        [RIO_AUDIT_LOG_HEADER]: reason,
                        'If-None-Match': '*', // if this header is not present, the request will fail with 404
                    },
                    responseHandler,
                });

                if (result.error) {
                    return { error: result.error as FetchBaseQueryError };
                }

                return { data: null };
            },
        }),
    }),
});

export const {
    useLazyFetchUserQuery,
    useLazyFetchAdminUsersQuery,
    useLazyFetchUsersByAccountIdQuery,
    useFetchAccountSettingQuery,
    useLazyFetchGroupsInAccountQuery,
    useInviteUserMutation,
    useDeleteUserMutation,
    useInviteAdminMutation,
} = usersApi;

interface UserBasics {
    firstName: string;
    lastName: string;
    accountId: string;
    groupIds: string[];
}

interface EmailAndPhone {
    email: string;
    phoneNumber: string;
}

type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> & U[keyof U];

type UserMerged = UserBasics &
    AtLeastOne<EmailAndPhone> & { accountType: string | undefined; preferredLanguage?: string };
